import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Briefcase, Plus, Send, MapPin, Trash2, X, UserCheck } from "lucide-react";
import { toast } from "sonner";

const JOB_TYPES = ["COACH", "FRONT_DESK", "OPERATIONS", "MARKETING", "ADMIN", "INTERN", "OTHER"];
const EMPLOYMENT_TYPES = ["FULL_TIME", "PART_TIME", "CONTRACT", "INTERN"];
const STATUS_COLOR = {
  PENDING: "#FFEA00", SHORTLISTED: "#7DD3FC", INTERVIEWING: "#A78BFA",
  HIRED: "#00E676", REJECTED: "#FF3B30",
};

export default function JobsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";

  const { data: jobs = [] } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => (await apiClient.get("/jobs")).data,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await apiClient.get("/branches")).data,
  });
  const { data: myRefs = [] } = useQuery({
    queryKey: ["my-job-referrals"],
    queryFn: async () => (await apiClient.get("/job-referrals")).data,
  });

  const closeJob = useMutation({
    mutationFn: async (id) => (await apiClient.patch(`/jobs/${id}`, { status: "CLOSED" })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); toast.success("Job closed"); },
  });
  const removeJob = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/jobs/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); toast.success("Job removed"); },
  });

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="jobs-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold flex items-center gap-2"><Briefcase size={12} /> Talent</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Open Roles</h1>
          <div className="text-zinc-500 text-sm mt-1">
            {isAdmin ? "Post openings. Members & coaches refer candidates. REPS credited on hire." : "Know someone perfect? Refer them and earn REPS when they're hired."}
          </div>
        </div>
        {user?.role === "SUPER_ADMIN" && (
          <NewJobDialog branches={branches} onSuccess={() => qc.invalidateQueries({ queryKey: ["jobs"] })} />
        )}
      </div>

      <Tabs defaultValue="board">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="board" data-testid="tab-board" className="data-[state=active]:bg-volt data-[state=active]:text-black">Open Roles ({jobs.filter(j => j.status === "OPEN").length})</TabsTrigger>
          <TabsTrigger value="myrefs" data-testid="tab-my-refs" className="data-[state=active]:bg-volt data-[state=active]:text-black">{isAdmin ? "All Candidate Referrals" : "My Referrals"} ({myRefs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="mt-4">
          {jobs.length === 0 ? (
            <Panel><div className="text-zinc-600 text-sm py-12 text-center">No open roles right now.</div></Panel>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {jobs.map((j) => {
                const branch = branches.find((b) => b.id === j.branch_id);
                return (
                  <div key={j.id} data-testid={`job-${j.id}`} className="bg-[#0c0c0c] border border-zinc-900 rounded-md p-5 inset-hi flex flex-col">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-300">{j.role_type.replace("_", " ")}</span>
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-300 ml-1">{j.employment_type.replace("_", " ")}</span>
                      </div>
                      {user?.role === "SUPER_ADMIN" && (
                        <div className="flex gap-1">
                          {j.status === "OPEN" && <button onClick={() => closeJob.mutate(j.id)} className="text-zinc-500 hover:text-yellow-400" title="Close" data-testid={`close-${j.id}`}><X size={14} /></button>}
                          <button onClick={() => { if (confirm("Delete this job and all its candidate referrals?")) removeJob.mutate(j.id); }} className="text-zinc-500 hover:text-red-400" title="Delete" data-testid={`delete-job-${j.id}`}><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                    <h3 className="font-display text-2xl font-black uppercase tracking-tight mt-2">{j.title}</h3>
                    {branch && <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1 flex items-center gap-1"><MapPin size={10} /> {branch.name}</div>}
                    <p className="text-xs text-zinc-400 mt-2 line-clamp-3 flex-1">{j.description}</p>
                    {j.salary_range && <div className="text-xs text-zinc-300 mt-2"><b>Pay:</b> {j.salary_range}</div>}
                    <div className="mt-4 pt-3 border-t border-zinc-900 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Referral reward</div>
                        <div className="font-display text-2xl font-black text-volt">{j.reps_reward} <span className="text-xs font-sans text-zinc-500">REPS</span></div>
                      </div>
                      {j.status === "OPEN" ? (
                        <ReferCandidateDialog job={j} onSuccess={() => qc.invalidateQueries({ queryKey: ["my-job-referrals"] })} />
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-sm bg-zinc-800 text-zinc-500">CLOSED</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="myrefs" className="mt-4">
          <Panel>
            {myRefs.length === 0 ? (
              <div className="text-zinc-600 text-sm py-12 text-center">No candidate referrals yet.</div>
            ) : (
              <div className="space-y-2">
                {myRefs.map((r) => (
                  <JobReferralRow key={r.id} item={r} isAdmin={isAdmin} onAction={() => qc.invalidateQueries({ queryKey: ["my-job-referrals"] })} />
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function JobReferralRow({ item, isAdmin, onAction }) {
  const update = useMutation({
    mutationFn: async (status) => (await apiClient.patch(`/job-referrals/${item.id}`, { status })).data,
    onSuccess: (d, status) => {
      onAction?.();
      if (status === "HIRED" && d.reps_awarded) toast.success(`Hired! ${d.reps_awarded} REPS credited to ${item.referrer_name || "referrer"}`);
      else toast.success(`Status: ${status}`);
    },
  });
  const color = STATUS_COLOR[item.status] || "#888";
  return (
    <div data-testid={`jobref-${item.id}`} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center gap-3">
      <div className="w-10 h-10 grid place-items-center rounded-sm bg-zinc-900 text-zinc-400"><UserCheck size={16} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{item.candidate_name}</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-mono-cmd">{item.candidate_phone}</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          For: <span className="text-zinc-300">{item.job_title}</span>
          {isAdmin && item.referrer_name && <span> · by <span className="text-zinc-300">{item.referrer_name}</span> ({item.referrer_type})</span>}
          {item.reps_awarded > 0 && <span className="text-volt"> · {item.reps_awarded} REPS awarded</span>}
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-sm" style={{ background: `${color}22`, color }}>{item.status}</span>
      {isAdmin && item.status !== "HIRED" && item.status !== "REJECTED" && (
        <Select value="" onValueChange={(v) => update.mutate(v)}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 w-40 h-8 text-xs uppercase" data-testid={`status-${item.id}`}>
            <SelectValue placeholder="Update Status" />
          </SelectTrigger>
          <SelectContent>
            {["SHORTLISTED", "INTERVIEWING", "HIRED", "REJECTED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function NewJobDialog({ branches, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    title: "", description: "", role_type: "COACH", employment_type: "FULL_TIME",
    branch_id: null, reps_reward: 5000, min_experience_years: 0, salary_range: "", requirements: "",
  });
  const submit = async () => {
    if (!f.title || !f.description) { toast.error("Title & description required"); return; }
    try {
      await apiClient.post("/jobs", { ...f, branch_id: f.branch_id || null });
      toast.success("Job posted — visible to members & coaches");
      setOpen(false); onSuccess?.();
      setF({ title: "", description: "", role_type: "COACH", employment_type: "FULL_TIME", branch_id: null, reps_reward: 5000, min_experience_years: 0, salary_range: "", requirements: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-job-button" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
          <Plus size={16} className="mr-1" /> Post Role
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-lg" data-testid="new-job-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Post a Role</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto scrollbar-thin">
          <Field label="Job title *"><Input data-testid="job-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="bg-zinc-950 border-zinc-800" /></Field>
          <Field label="Description *"><Textarea data-testid="job-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} className="bg-zinc-950 border-zinc-800" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <Select value={f.role_type} onValueChange={(v) => setF({ ...f, role_type: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>{JOB_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Employment">
              <Select value={f.employment_type} onValueChange={(v) => setF({ ...f, employment_type: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Branch (optional — leave blank for any)">
            <Select value={f.branch_id || "ANY"} onValueChange={(v) => setF({ ...f, branch_id: v === "ANY" ? null : v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ANY">Any branch</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Referrer REPS reward (on hire)">
              <Input data-testid="job-reps-reward" type="number" value={f.reps_reward} onChange={(e) => setF({ ...f, reps_reward: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800" />
            </Field>
            <Field label="Salary range">
              <Input value={f.salary_range} onChange={(e) => setF({ ...f, salary_range: e.target.value })} placeholder="₹40-60k / month" className="bg-zinc-950 border-zinc-800" />
            </Field>
          </div>
          <Field label="Requirements / qualifications">
            <Textarea value={f.requirements} onChange={(e) => setF({ ...f, requirements: e.target.value })} rows={2} placeholder="e.g. ACE/NASM certified, 2+ yrs strength training experience" className="bg-zinc-950 border-zinc-800" />
          </Field>
          <Button onClick={submit} data-testid="submit-job" className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">Post Role</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReferCandidateDialog({ job, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ candidate_name: "", candidate_phone: "", candidate_email: "", resume_url: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState("");
  const fileRef = React.useRef(null);

  const submit = async () => {
    if (!f.candidate_name || !f.candidate_phone) { toast.error("Name & phone required"); return; }
    try {
      await apiClient.post(`/jobs/${job.id}/refer`, f);
      toast.success(`Candidate submitted — you'll earn ${job.reps_reward} REPS if they're hired.`);
      setOpen(false); onSuccess?.();
      setF({ candidate_name: "", candidate_phone: "", candidate_email: "", resume_url: "", notes: "" });
      setUploadedName("");
    } catch (e) { toast.error("Failed to submit"); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/jobs/referrals/upload-resume", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF((prev) => ({ ...prev, resume_url: data.url }));
      setUploadedName(file.name);
      toast.success(data.mocked ? `Resume uploaded (MOCKED · local storage)` : "Resume uploaded to Drive");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`refer-job-${job.id}`} size="sm" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase rounded-sm">
          <Send size={12} className="mr-1" /> Refer
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-md" data-testid="refer-candidate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase">Refer candidate for {job.title}</DialogTitle>
        </DialogHeader>
        <div className="bg-volt/10 border border-volt/30 text-volt text-xs uppercase tracking-[0.16em] px-3 py-2 rounded-sm">
          Earn <span className="font-bold">{job.reps_reward} REPS</span> when this candidate is hired.
        </div>
        <div className="space-y-3">
          <Field label="Candidate name *"><Input data-testid="cand-name" value={f.candidate_name} onChange={(e) => setF({ ...f, candidate_name: e.target.value })} className="bg-zinc-950 border-zinc-800" /></Field>
          <Field label="Phone *"><Input data-testid="cand-phone" value={f.candidate_phone} onChange={(e) => setF({ ...f, candidate_phone: e.target.value })} placeholder="+91 9..." className="bg-zinc-950 border-zinc-800" /></Field>
          <Field label="Email"><Input value={f.candidate_email} onChange={(e) => setF({ ...f, candidate_email: e.target.value })} className="bg-zinc-950 border-zinc-800" /></Field>

          <Field label="Resume (PDF · DOC · DOCX · max 8 MB)">
            <div className="space-y-2">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFile}
                data-testid="resume-upload-input"
                className="block w-full text-xs text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-[10px] file:uppercase file:tracking-[0.14em] file:bg-volt file:text-black file:cursor-pointer hover:file:bg-volt-hover"
                disabled={uploading}
              />
              {uploading && <div className="text-[10px] uppercase tracking-[0.16em] text-volt">Uploading…</div>}
              {uploadedName && !uploading && (
                <div className="text-[10px] uppercase tracking-[0.16em] text-volt" data-testid="resume-uploaded-name">
                  ✓ {uploadedName}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">— or paste a link —</div>
              <Input
                value={f.resume_url}
                onChange={(e) => setF({ ...f, resume_url: e.target.value })}
                placeholder="Google Drive / LinkedIn / …"
                className="bg-zinc-950 border-zinc-800"
                data-testid="resume-url-input"
              />
            </div>
          </Field>

          <Field label="Why are they a fit?"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="bg-zinc-950 border-zinc-800" /></Field>
          <Button onClick={submit} disabled={uploading} data-testid="submit-candidate" className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">Submit Candidate</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
